import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, HeartIcon, ShareIcon, MapPinIcon, ShieldCheckIcon, SparklesIcon, MessageSquareIcon, PhoneIcon, CheckCircle2Icon, SendIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const mockListing = {
  id: '1',
  title: 'Toyota Land Cruiser Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: ['https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=1200', 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=1200', 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=1200'],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Engine',
    value: '4.0L V6'
  }, {
    label: 'Transmission',
    value: 'Automatic'
  }, {
    label: 'Exterior Color',
    value: 'Pearl White'
  }, {
    label: 'Interior Color',
    value: 'Beige'
  }, {
    label: 'Body Type',
    value: 'SUV'
  }],
  matchScore: 98,
  seller: {
    name: 'Al Futtaim Automall',
    type: 'Verified Business',
    joined: '2015',
    responseRate: '99%',
    responseTime: 'within 1 hour'
  },
  description: 'Excellent condition Toyota Prado GXR 2018. Full service history available. No accidents, original paint. Comes with 1 year warranty and free service contract for 20,000 km. Finance options available.',
  aiSummary: ['Fair price based on market comparison (avg AED 84k)', 'GCC specs confirmed via VIN', 'Mileage is reasonable for the model year', 'Seller response rate is exceptionally high']
};
export function ListingDetail() {
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiChat, setAiChat] = useState<{
    role: 'user' | 'ai';
    content: string;
  }[]>([]);
  const handleAskAI = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    setAiChat([...aiChat, {
      role: 'user',
      content: aiQuery
    }]);
    const query = aiQuery;
    setAiQuery('');
    setTimeout(() => {
      let response = 'Based on the listing details, this is a very competitive offer. The price is slightly below market average for a 2018 GXR with this mileage.';
      if (query.toLowerCase().includes('check')) {
        response = 'Before buying, you should check the service history book, inspect the tires (they usually need replacement around 100k km), and verify the warranty terms mentioned in the description.';
      }
      setAiChat(prev => [...prev, {
        role: 'ai',
        content: response
      }]);
    }, 1000);
  };
  return <div className="flex-1 overflow-y-auto bg-slate-50 pb-24 lg:pb-8" data-id="element-5531" data-mp="mlh5swue8x9j">
      {/* Top Navigation */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between lg:hidden" data-id="element-5532" data-mp="cttgp2lb7sb8">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-5533" data-mp="7tyrr4l3hcst">
          <ChevronLeftIcon className="w-6 h-6 text-slate-700" data-id="element-5534" data-mp="3ug2xxq5khkp" />
        </button>
        <div className="flex gap-2" data-id="element-5535" data-mp="3uxv1rtca87hs">
          <button className="p-2 hover:bg-slate-100 rounded-full" data-id="element-5536" data-mp="3v1skweyv0z1o">
            <ShareIcon className="w-5 h-5 text-slate-700" data-id="element-5537" data-mp="3q0v4o7xeuf2t" />
          </button>
          <button onClick={() => setIsSaved(!isSaved)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-5538" data-mp="3l2bjwaujz9c1">
            <HeartIcon className={`w-5 h-5 ${isSaved ? 'fill-error text-error' : 'text-slate-700'}`} data-id="element-5539" data-mp="1o6vujpw0y3rp" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-8" data-id="element-5540" data-mp="1rb4gudjcd2xb">
        {/* Desktop Back Button */}
        <button onClick={() => navigate(-1)} className="hidden lg:flex items-center gap-2 text-slate-500 hover:text-primary-indigo mb-6 transition-colors" data-id="element-5541" data-mp="1w8d1ckrypj1q">
          <ChevronLeftIcon className="w-5 h-5" data-id="element-5542" data-mp="1map2binbppdr" />
          <span data-id="element-5543" data-mp="1mdkzv5v9ec1o">Back to results</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-5544" data-mp="18j26tl17gkw1">
          {/* Left Column: Images & Details */}
          <div className="lg:col-span-2 space-y-8" data-id="element-5545" data-mp="1dbligcgc15y7">
            {/* Image Gallery */}
            <div className="space-y-4" data-id="element-5546" data-mp="ymyujmcn35vd">
              <div className="aspect-[4/3] lg:aspect-[16/9] rounded-2xl overflow-hidden bg-slate-200 relative group" data-id="element-5547" data-mp="yjt8kfzb4k3v">
                <img src={mockListing.images[activeImage]} alt={mockListing.title} className="w-full h-full object-cover" data-id="element-5548" data-mp="pl7twvwyhp8d" />

                {/* Desktop Actions Overlay */}
                <div className="hidden lg:flex absolute top-4 right-4 gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5549" data-mp="azbqn141ej5v">
                  <button className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-5550" data-mp="61ioqk9robbe">
                    <ShareIcon className="w-5 h-5 text-slate-700" data-id="element-5551" data-mp="f0dvpykmxpfr" />
                  </button>
                  <button onClick={() => setIsSaved(!isSaved)} className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" data-id="element-5552" data-mp="tx4uc5mhix4b">
                    <HeartIcon className={`w-5 h-5 ${isSaved ? 'fill-error text-error' : 'text-slate-700'}`} data-id="element-5553" data-mp="12on8yc3s94va" />
                  </button>
                </div>
              </div>

              <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar" data-id="element-5554" data-mp="1coe48g063nwf">
                {mockListing.images.map((img, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 transition-all ${activeImage === idx ? 'border-primary-indigo' : 'border-transparent opacity-70 hover:opacity-100'}`} data-id="element-5555" data-mp="17mylwvpdjvhq" data-mpi={idx}>
                    <img src={img} alt={`Thumbnail ${idx}`} className="w-full h-full object-cover" data-id="element-5556" data-mp="j3jw39v6wgcq" />
                  </button>)}
              </div>
            </div>

            {/* Title & Price Mobile (Hidden on Desktop) */}
            <div className="lg:hidden space-y-4" data-id="element-5557" data-mp="x0gj1086tdoc">
              <div className="flex justify-between items-start gap-4" data-id="element-5558" data-mp="121w7715nw0ay">
                <h1 className="font-display font-bold text-2xl text-dark-navy" data-id="element-5559" data-mp="2ytxrg6456ik5">
                  {mockListing.title}
                </h1>
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-primary-indigo/10 to-ai-purple/10 px-3 py-1.5 rounded-full border border-primary-indigo/20" data-id="element-5560" data-mp="33u25zqlhp37q">
                  <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-5561" data-mp="2ri9kz5xuwjgi" />
                  <span className="text-sm font-bold text-primary-indigo" data-id="element-5562" data-mp="2rgyhl78bpdpx">
                    {mockListing.matchScore}%
                  </span>
                </div>
              </div>
              <div className="font-bold text-3xl text-primary-indigo" data-id="element-5563" data-mp="2hmetrmfkhg24">
                {mockListing.price}
              </div>
              <div className="flex items-center gap-2 text-slate-500" data-id="element-5564" data-mp="3a99dgh4k8bej">
                <MapPinIcon className="w-5 h-5" data-id="element-5565" data-mp="3a76f99t97byq" />
                <span data-id="element-5566" data-mp="359eowk89bxwn">{mockListing.location}</span>
              </div>
            </div>

            {/* AI Summary Box */}
            <div className="bg-gradient-to-br from-primary-indigo/5 via-ai-purple/5 to-electric-cyan/5 rounded-2xl p-6 border border-primary-indigo/10 ai-glow" data-id="element-5567" data-mp="317t50exb2jcy">
              <div className="flex items-center gap-3 mb-4" data-id="element-5568" data-mp="1pto0nkt6k7h3">
                <div className="p-2 bg-gradient-to-br from-primary-indigo to-ai-purple rounded-xl" data-id="element-5569" data-mp="1pvikevspttjl">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-5570" data-mp="3p0a8h22st0d7" />
                </div>
                <h3 className="font-display font-bold text-lg text-dark-navy" data-id="element-5571" data-mp="3tyk4tg09e70e">
                  AI Analysis
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-5572" data-mp="3kw2hey8vb8bt">
                {mockListing.aiSummary.map((item, idx) => <li key={idx} className="flex items-start gap-3 text-slate-700" data-id="element-5573" data-mp="cg92gnu6q4wv" data-mpi={idx}>
                    <CheckCircle2Icon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-5574" data-mp="ghuhofu6yva8" />
                    <span data-id="element-5575" data-mp="2qg08dfxn25a">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Key Information Grid */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-5576" data-mp="vf3gfultr1bz">
              <h3 className="font-display font-bold text-xl text-dark-navy mb-6" data-id="element-5577" data-mp="1063cyyr17hdy">
                Key Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-5578" data-mp="19k3tfrjvnm1x">
                {mockListing.specs.map((spec, idx) => <div key={idx} data-id="element-5579" data-mp="15jbjew36ld4l" data-mpi={idx}>
                    <span className="block text-sm text-slate-500 mb-1" data-id="element-5580" data-mp="1jaozu15sluve">
                      {spec.label}
                    </span>
                    <span className="font-medium text-slate-900" data-id="element-5581" data-mp="1ecwyyhn66yf2">
                      {spec.value}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-5582" data-mp="mmsi2symctil">
              <h3 className="font-display font-bold text-xl text-dark-navy mb-4" data-id="element-5583" data-mp="10ls0vjzgae6u">
                Description
              </h3>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-5584" data-mp="2bznlv9d1dh5z">
                {mockListing.description}
              </p>
            </div>
          </div>

          {/* Right Column: Sticky Sidebar */}
          <div className="space-y-6" data-id="element-5585" data-mp="gvg8u4dpjdmu">
            <div className="sticky top-24 space-y-6" data-id="element-5586" data-mp="guewzxovan42">
              {/* Title & Price Desktop (Hidden on Mobile) */}
              <div className="hidden lg:block bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4" data-id="element-5587" data-mp="by6kks22180l">
                <div className="flex justify-between items-start gap-4" data-id="element-5588" data-mp="26af2vxn2fmc">
                  <h1 className="font-display font-bold text-2xl text-dark-navy" data-id="element-5589" data-mp="247pgd0eeoi5">
                    {mockListing.title}
                  </h1>
                  <div className="flex items-center gap-1.5 bg-gradient-to-r from-primary-indigo/10 to-ai-purple/10 px-3 py-1.5 rounded-full border border-primary-indigo/20 flex-shrink-0" data-id="element-5590" data-mp="3pagotzzoavu7">
                    <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-5591" data-mp="3fdc2o05tv0de" />
                    <span className="text-sm font-bold text-primary-indigo" data-id="element-5592" data-mp="3ff6odgoxawij">
                      {mockListing.matchScore}%
                    </span>
                  </div>
                </div>
                <div className="font-bold text-3xl text-primary-indigo" data-id="element-5593" data-mp="1djcw1s3nh771">
                  {mockListing.price}
                </div>
                <div className="flex items-center gap-2 text-slate-500" data-id="element-5594" data-mp="1q3jhrhahj5po">
                  <MapPinIcon className="w-5 h-5" data-id="element-5595" data-mp="1q1ozkaqllrpa" />
                  <span data-id="element-5596" data-mp="1uw2vqfd342v0">{mockListing.location}</span>
                </div>
              </div>

              {/* Seller Card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200" data-id="element-5597" data-mp="1zvz0whxiq0c5">
                <div className="flex items-center gap-4 mb-6" data-id="element-5598" data-mp="135ewx9yvv9ki">
                  <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center text-xl font-bold text-slate-400" data-id="element-5599" data-mp="174feyq9je86g">
                    {mockListing.seller.name.charAt(0)}
                  </div>
                  <div data-id="element-5600" data-mp="1h4674lamkb3d">
                    <h3 className="font-bold text-lg text-dark-navy" data-id="element-5601" data-mp="1h1a80tn1jgdc">
                      {mockListing.seller.name}
                    </h3>
                    <div className="flex items-center gap-1.5 text-sm mt-1" data-id="element-5602" data-mp="2si0i4nul4sc8">
                      <ShieldCheckIcon className="w-4 h-4 text-success" data-id="element-5603" data-mp="22ueb6gg7inzt" />
                      <span className="font-medium text-success" data-id="element-5604" data-mp="22whbpuezjzis">
                        {mockListing.seller.type}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6 text-sm" data-id="element-5605" data-mp="2cq7x2qftftu9">
                  <div className="bg-slate-50 p-3 rounded-xl" data-id="element-5606" data-mp="27u0sp232iqef">
                    <span className="block text-slate-500 mb-1" data-id="element-5607" data-mp="1k33pcdpc91es">
                      Response Rate
                    </span>
                    <span className="font-medium text-slate-900" data-id="element-5608" data-mp="1f6d8ho5pm1sb">
                      {mockListing.seller.responseRate}
                    </span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-xl" data-id="element-5609" data-mp="1p2pyoc21pxib">
                    <span className="block text-slate-500 mb-1" data-id="element-5610" data-mp="11df81neetjhw">Replies</span>
                    <span className="font-medium text-slate-900" data-id="element-5611" data-mp="11bauedqpl64f">
                      {mockListing.seller.responseTime}
                    </span>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-5612" data-mp="380cvxa5b2bne">
                  <button className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors shadow-sm" data-id="element-5613" data-mp="3csw7k1kfmwpk">
                    <MessageSquareIcon className="w-5 h-5" data-id="element-5614" data-mp="3321k4tvg9r2j" />
                    Message Seller
                  </button>
                  <button className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#25D366] text-white rounded-xl font-medium hover:bg-[#20BD5A] transition-colors shadow-sm" data-id="element-5615" data-mp="3qt71xekyatfh">
                    <PhoneIcon className="w-5 h-5" data-id="element-5616" data-mp="3hs8u0l7e61rm" />
                    WhatsApp
                  </button>
                </div>
              </div>

              {/* Ask AI Chat Box */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[400px]" data-id="element-5617" data-mp="26bsmde22wpfb">
                <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-primary-indigo/5 to-ai-purple/5 flex items-center gap-2" data-id="element-5618" data-mp="2aeoxkjpahj98">
                  <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-5619" data-mp="2qmv5myduhomc" />
                  <h3 className="font-display font-bold text-dark-navy" data-id="element-5620" data-mp="2lsqxibsufajo">
                    Ask AI about this listing
                  </h3>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50" data-id="element-5621" data-mp="2bwnsmy6lwxt1">
                  {aiChat.length === 0 ? <div className="space-y-2" data-id="element-5622" data-mp="39je6l0ehw67t">
                      <p className="text-sm text-slate-500 mb-3" data-id="element-5623" data-mp="34l2wqpo79vpl">
                        Suggested questions:
                      </p>
                      {['Is this overpriced?', 'Compare this with my saved car', 'What should I check before buying?'].map((q, i) => <button key={i} onClick={() => setAiQuery(q)} className="block w-full text-left px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-5624" data-mp="1o4gtjr9hycpx" data-mpi={i}>
                          {q}
                        </button>)}
                    </div> : aiChat.map((msg, idx) => <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-5625" data-mp="ie84t37ayngv" data-mpi={idx}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-primary-indigo text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-none shadow-sm'}`} data-id="element-5626" data-mp="red2glmp27hu">
                          {msg.content}
                        </div>
                      </div>)}
                </div>

                <form onSubmit={handleAskAI} className="p-3 border-t border-slate-100 bg-white" data-id="element-5627" data-mp="5ambvdhjm9tt">
                  <div className="relative" data-id="element-5628" data-mp="eaixt7cjsul6">
                    <input type="text" value={aiQuery} onChange={e => setAiQuery(e.target.value)} placeholder="Ask anything..." className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/10 transition-all" data-id="element-5629" data-mp="ecc4gmn5vet6" />
                    <button type="submit" disabled={!aiQuery.trim()} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 text-primary-indigo hover:bg-primary-indigo/10 rounded-lg transition-colors disabled:opacity-50" data-id="element-5630" data-mp="sz598xz21274">
                      <SendIcon className="w-4 h-4" data-id="element-5631" data-mp="1llvkxbf0u98a" />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}